import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValue } from '../../shared/component/headless/input/InputBody';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { Header as HeaderRow, Title } from '../../shared/component/modal/SearchModalLayout';
import deleteIcon from '../../asset/icon/delete.png';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { AlertPosition, AlertType, TextAlignment } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import {
  getPreferredProjectInfoLinkUrl,
  isValidProjectInfoLinkId,
  parseProjectInfoLink,
} from '../../shared/util/projectInfo';

interface ProjectInfoUrlModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const ProjectInfoUrlModal: React.FC<ProjectInfoUrlModalProps> = ({ setIsUserInput }) => {
  const { standardModal, clearModal } = useModalStore();
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);
  const initialLink = parseProjectInfoLink(standardModal?.value ?? null);
  const [architraveId, setArchitraveId] = useState(
    initialLink?.platform === 'architrave' ? initialLink.id : '',
  );
  const [instagramId, setInstagramId] = useState(
    initialLink?.platform === 'instagram' ? initialLink.id : '',
  );

  useEffect(() => {
    setIsUserInput(Boolean(architraveId || instagramId));
  }, [architraveId, instagramId, setIsUserInput]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      setIsUserInput(false);
    };
  }, [clearModal, setIsUserInput]);

  const handleConfirm = (event: React.FormEvent) => {
    event.preventDefault();
    const hasInvalidId =
      (Boolean(architraveId.trim()) && !isValidProjectInfoLinkId('architrave', architraveId)) ||
      (Boolean(instagramId.trim()) && !isValidProjectInfoLinkId('instagram', instagramId));
    if (hasInvalidId) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_PROJECT_INFO_URL_FORMAT'),
      });
      return;
    }
    const nextUrl = getPreferredProjectInfoLinkUrl(architraveId, instagramId);
    standardModal?.handleChange(nextUrl);
    clearModal();
  };

  return (
    <Container onSubmit={handleConfirm} noValidate>
      <HeaderRow>
        <Title>{getHintMessage('CONNECT_URL')}</Title>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          onClick={() => clearModal()}
          aria-label="Close"
        />
      </HeaderRow>
      <InputList>
        <InputRow>
          <InputLabel>architrave</InputLabel>
          <HeadlessInput
            type="text"
            value={architraveId}
            handleChange={(event) => setArchitraveId(event.target.value.toLowerCase())}
            placeholder=""
            StyledInput={ArchitraveInput}
            $alignment={TextAlignment.LEFT}
          />
        </InputRow>
        <InputRow>
          <InputLabel>instagram</InputLabel>
          <HeadlessInput
            type="text"
            value={instagramId}
            handleChange={(event) => setInstagramId(event.target.value)}
            placeholder=""
            StyledInput={InstagramInput}
            $alignment={TextAlignment.LEFT}
          />
        </InputRow>
      </InputList>
      <BtnContainer>
        <BlackFitBtn type="submit">{getBtnName('OK')}</BlackFitBtn>
      </BtnContainer>
    </Container>
  );
};

export default ProjectInfoUrlModal;

const Container = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0.5vw;
`;

const InputList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 100px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
`;

const InputLabel = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const UrlInput = styled(MemberInfoValue).attrs({
  maxLength: 31,
})``;

const ArchitraveInput = styled(UrlInput).attrs({
  'aria-label': 'Architrave ID',
  autoFocus: true,
})``;

const InstagramInput = styled(UrlInput).attrs({
  'aria-label': 'Instagram ID',
})``;

const BtnContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2.5vh;
`;
